'use client';
import cn from 'classnames';
import {
  ChangeEvent,
  Dispatch,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo
} from 'react';

import { EllaAction, EllaState } from '../Ella/Ella';
import {
  removeItemFromCart,
  fetchVivrelleCart,
  getGenericCart,
  getGenericCartItems,
  submitRevolveOrder,
  getOrder,
  processRevolveOrderResponse,
  cancelRevolveOrders
} from '@/util/cart_generic_client';

import EllaCartProduct, {
  ELLA_RETAILER,
  EllaProductData
} from '@/components/common/EllaCartProduct/EllaCartProduct';
import EllaCartShippingBilling from '@/components/common/EllaCartShippingBilling/EllaCartShippingBilling';
import EllaCartSubtotal from '@/components/common/EllaCartSubtotal/EllaCartSubtotal';
import Button from '@/components/common/Button/Button';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';
import Select from '@/components/common/Select/Select';
import Text from '@/components/common/Text/Text';
import { useEllaAnalytics } from '@/components/hook/analytics';
import { useUser } from '@/components/hook/user';

import { EPCCAddress } from '@/types/User';
import { ShippingMethod } from '@/types/ShippingMethods';
import {
  getAdditionalCheckoutFieldHelperText,
  getAdditionalCheckoutFieldLabel,
  MAX_ADDITIONAL_CHECKOUT_FIELD_LENGTH,
  parseFixedShippingAddress
} from '@/util/shipping_method_checkout';

import '@/components/form/lib/InputField/InputField.style.scss';
import './EllaCart.style.scss';
import {
  createInvoice,
  payInvoice,
  restoreBalanceAndDiscounts
} from '@/util/stripe_client';

import { CheckoutRequest } from '@/app/api/cart/checkout/route';
import { Order, RevolveOrderItem } from '@/types/Order';

import analytics from '@/util/analytics';
import toast from 'react-hot-toast';
import dynamic from 'next/dynamic';
import { CreateInvoiceResponse } from '@/app/api/stripe/invoices/create/route';
import { usePayment } from '@/components/hook/payment';
import Stripe from 'stripe';

type EllaCartProps = {
  /** Member's addresses, including billing and shipping */
  addresses: EPCCAddress[];
  className?: string;
  dispatch: Dispatch<EllaAction>;
  errorMessage?: string | null;
  shippingMethods: ShippingMethod[];
  state: EllaState;
};

const EllaCartSkeleton = dynamic(() => import('./EllaCartSkeleton'), {
  ssr: false
});

const EllaCart = (props: EllaCartProps): JSX.Element => {
  const {
    className = '',
    dispatch,
    addresses,
    shippingMethods = [],
    state
  } = props;

  const [errorMessage, setErrorMessage] = useState<string>('');
  /** Answer to the selected shipping method's additional checkout field */
  const [additionalCheckoutFieldValue, setAdditionalCheckoutFieldValue] =
    useState<string>('');
  const [isPlacingOrder, setIsPlacingOrder] = useState<boolean>(false);
  const isPlacingOrderRef = useRef(false);
  const [removingItemId, setRemovingItemId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [newlyAttachedPaymentMethod, setNewlyAttachedPaymentMethod] =
    useState<Stripe.PaymentMethod | null>(null);

  const {
    value: { customer: paymentCustomer, method: paymentMethod },
    actions: { refreshPayment }
  } = usePayment();

  const paymentCard = useMemo(() => {
    // If user has just attached a new payment method, return that
    if (newlyAttachedPaymentMethod) {
      return newlyAttachedPaymentMethod.card;
    }

    // Otherwise use payment method (if defined)
    if (paymentMethod) {
      return paymentMethod.card;
    }

    // If paymentMethod not defined, it's likely a legacy customer whose default source is attached to customer
    if (paymentCustomer) {
      return paymentCustomer.default_source;
    }
  }, [paymentMethod, paymentCustomer, newlyAttachedPaymentMethod]);

  // If payment card is not defined, try calling refresh method from context provider
  useEffect(() => {
    if (!paymentCard) {
      refreshPayment();
    }
  }, [refreshPayment, paymentCard]);

  const { value: user } = useUser();
  const { metadata } = useEllaAnalytics();

  const setErrorMessageTemporarily = (msg: string) => {
    setErrorMessage(msg);
    setTimeout(() => {
      setErrorMessage('');
    }, 3000);
  };

  const [cartItems, setCartItems] = useState<EllaProductData[]>([]);

  const fetchCart = useCallback(async () => {
    setIsLoading(true);
    const getVivrelleCart = async () => {
      const cart = await fetchVivrelleCart();
      const vivrelleCart = cart.items.map((item: any) => ({
        name: item.productCardData.name,
        provider: ELLA_RETAILER.VIVRELLE,
        price: item.productCardData.price || 0,
        title: '',
        category: item.productCardData.category,
        sku: item.productCardData.sku,
        image: item.productCardData.mainImage,
        id: item.id,
        link: `/products/${item.slug}`,
        subcategory: '',
        subsubcategory: '',
        score: 0,
        allSizes: [],
        retail_price: 0,
        mainImage: item.productCardData.mainImage,
        item_group_id: item.id,
        description: '',
        common_color: item.productCardData.common_color,
        product_type: '',
        tier: ''
      }));

      return vivrelleCart;
    };
    const vivrelleCart = await getVivrelleCart();
    let items = vivrelleCart;

    if (state.revolveCartId) {
      try {
        const cartItemsResponse = await getGenericCartItems(
          state.revolveCartId
        );

        const checkoutResponse = await getGenericCart(state.revolveCartId);
        if (checkoutResponse.data.errors) {
          setCartItems([]);
          dispatch({
            type: 'SET_BILL',
            payload: {
              bill: {
                ...state.bill,
                subtotal: 0,
                taxes: 0
              }
            }
          });
          return;
        }
        const displayPrice = checkoutResponse.data.meta.display_price;

        const subtotal = Number(displayPrice.without_tax.amount);

        const taxes = Number(displayPrice.tax.amount);

        dispatch({
          type: 'SET_BILL',
          payload: {
            bill: {
              ...state.bill,
              subtotal,
              taxes: taxes
            }
          }
        });

        items = [
          ...vivrelleCart,
          ...cartItemsResponse.data.map((item: any) => ({
            name: item.name,
            provider: item.custom_inputs?.product?.store,
            price: item.value.amount,
            size: item.custom_inputs?.size,
            title: item.name,
            score: 0,
            allSizes: item.custom_inputs?.product?.allSizes
              ? item.custom_inputs.product.allSizes
                  ?.toString()
                  .replace(/[\[\]\s]/g, '')
                  .split(',')
              : [],
            link: item.custom_inputs?.product?.link,
            cj_link: item.custom_inputs?.product?.cj_link,
            image:
              item.custom_inputs?.product?.mainImage ||
              item.custom_inputs?.product?.image,
            mainImage: item.custom_inputs?.product?.mainImage,
            id: item.id,
            revolveCartId: state.revolveCartId,
            taxItemId: item.relationships?.taxes?.data?.[0]?.id,
            sku: item.custom_inputs?.product?.sku,
            item_group_id: item.id,
            common_color: item.custom_inputs?.product?.common_color,
            brand: item.custom_inputs?.product?.brand,
            retail_price: item.custom_inputs?.product?.retail_price,
            product_type: item.custom_inputs?.product?.product_type,
            category: item.custom_inputs?.product?.category,
            subcategory: item.custom_inputs?.product?.sub_category,
            subsubcategory: item.custom_inputs?.product?.sub_sub_category,
            year: item.custom_inputs?.product?.year
          }))
        ];
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    }
    setCartItems(items);
    setIsLoading(false);

    // Check if cart is empty and close cart if so
    if (items.length === 0) {
      dispatch({
        type: 'SET_SHOW_CART',
        payload: { showCart: false }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.bill, state.revolveCartId, addresses, state.cart]);

  useEffect(() => {
    if (user) {
      fetchCart();
    }
  }, []); // Do not add user in dependency because userActions.refresh() will refresh the whole state

  const vivrelleCart = useMemo(() => {
    return cartItems.filter((item) => item.provider === ELLA_RETAILER.VIVRELLE);
  }, [cartItems]);

  const revolveCart = useMemo(() => {
    return cartItems.filter((item) => item.provider !== ELLA_RETAILER.VIVRELLE);
  }, [cartItems]);

  const handleUpdateItemSize = useCallback((id: string, size: string) => {
    setCartItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, size } : item))
    );
  }, []);

  const handleRemoveItem = async (item: EllaProductData) => {
    if (!item.id) return;

    setRemovingItemId(item.id);

    try {
      if (item.provider?.match(/vivrelle/i)) {
        // When the user removes the Vivrelle item from the cart we need update the shipping selected
        // This will guarantee if the vivrelle item was removed the shipping will be reseted
        dispatch({
          type: 'SET_SHIPPING_METHOD',
          payload: {
            shippingMethodId: '',
            shippingMethodCopy: ''
          }
        });
        await removeItemFromCart(undefined, item.id, item.store);
      } else {
        await removeItemFromCart(state.revolveCartId, item.id, item.store);
      }

      dispatch({
        type: 'REMOVE_FROM_CART',
        payload: { cartItemId: item.id }
      });

      await fetchCart();
      toast.success('Item removed from cart');
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error('Failed to remove item from cart');
    } finally {
      setRemovingItemId(null);
    }
  };

  const handleShippingSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.currentTarget;
    const shippingMethod = shippingMethods.find(
      (method) => method.id === value
    );
    dispatch({
      type: 'SET_SHIPPING_METHOD',
      payload: {
        shippingMethodId: shippingMethod?.id || '',
        shippingMethodCopy: shippingMethod?.label || ''
      }
    });

    analytics.track('Ella AI Checkout Shipping Type - Updated', {
      ...metadata,
      shippingType: shippingMethod
    });

    dispatch({
      type: 'SET_BILL',
      payload: {
        bill: {
          ...state.bill,
          shipping: Number((shippingMethod?.price || 0) * 100)
        }
      }
    });
  };

  // An answer belongs to one method's question; don't carry it to another.
  // Keyed on the parent's selection rather than handleShippingSelect, because
  // Ella also changes the method itself (default selection, removing items).
  useEffect(() => {
    setAdditionalCheckoutFieldValue('');
  }, [state.shippingMethodId]);

  const selectedShippingMethod = shippingMethods.find(
    (method) => method.id === state.shippingMethodId
  );
  const additionalCheckoutFieldLabel = getAdditionalCheckoutFieldLabel(
    selectedShippingMethod
  );
  const additionalCheckoutFieldHelperText =
    getAdditionalCheckoutFieldHelperText(selectedShippingMethod);
  // The Vivrelle order ships here (the BFF swaps it in); the shipping/billing
  // panel shows it as the Vivrelle destination.
  const fixedShippingAddress = parseFixedShippingAddress(
    selectedShippingMethod
  ).address;
  // Only the Vivrelle order uses the shipping method, so a Revolve/FWRD-only
  // cart never needs the answer.
  const isAdditionalCheckoutFieldMissing =
    vivrelleCart.length > 0 &&
    !!additionalCheckoutFieldLabel &&
    !additionalCheckoutFieldValue.trim();

  const handleOrder = async () => {
    if (isPlacingOrderRef.current) return;
    isPlacingOrderRef.current = true;
    try {
      await handleOrderUnguarded();
    } finally {
      isPlacingOrderRef.current = false;
    }
  };

  const handleOrderUnguarded = async () => {
    setIsPlacingOrder(true);

    // This should be impossible since button is disabled for this same state, but still worth a sanity check
    if (!user || !user.paymentCustomerId) {
      setErrorMessageTemporarily('User is not logged in and cannot checkout');
      setIsPlacingOrder(false);
      return;
    }

    if (isAdditionalCheckoutFieldMissing) {
      setErrorMessageTemporarily(`${additionalCheckoutFieldLabel} is required`);
      setIsPlacingOrder(false);
      return;
    }

    // Prefer default addresses: billing changes create new records and demote
    // the previous default, so first-match can hit a stale historical record.
    const shippingAddress =
      addresses.find(
        (addr: EPCCAddress) => !addr.isBilling && addr.isDefault
      ) ?? addresses.find((addr: EPCCAddress) => !addr.isBilling);

    if (!shippingAddress) {
      throw new Error('No shipping address available');
    }

    let revolveOrder: Order | undefined;
    let vivrelleOrder: Order | undefined;
    let errorSubmitOrder = '';
    let fwrdInvoice: CreateInvoiceResponse | undefined;
    let revolveInvoice: CreateInvoiceResponse | undefined;

    try {
      // Vivrelle order
      if (vivrelleCart.length > 0) {
        const checkoutData: CheckoutRequest = {
          shipping_address: {
            id: shippingAddress.id,
            firstName: shippingAddress.first_name || '',
            lastName: shippingAddress.last_name || '',
            phoneNumber: shippingAddress.phone_number || '',
            addressLine1: shippingAddress.line_1 || '',
            addressLine2: shippingAddress.line_2 || '',
            city: shippingAddress.city || '',
            state: shippingAddress.region || '',
            zipCode: shippingAddress.postcode || '',
            country: shippingAddress.country || 'US'
          },
          shipping_method: state.shippingMethodId || 'standard',
          ...(additionalCheckoutFieldLabel && {
            additional_checkout_field_value: additionalCheckoutFieldValue.trim()
          }),
          channel: 'website'
        };
        const checkoutResponse = await fetch('/api/cart/checkout', {
          method: 'POST',
          body: JSON.stringify(checkoutData),
          headers: {
            'content-type': 'application/json'
          },
          next: {
            revalidate: 0
          }
        });

        const checkoutResult = await checkoutResponse.json();

        // A member-facing rejection (e.g. the shipping option's required field)
        // stops the whole checkout with its reason, as any Vivrelle failure
        // already stopped it, just with a generic message.
        if (!checkoutResult.success && checkoutResult.message) {
          setErrorMessageTemporarily(checkoutResult.message);
          setIsPlacingOrder(false);
          return;
        }
        if (!checkoutResult.success) {
          setIsPlacingOrder(false);
          errorSubmitOrder = 'Vivrelle';
        }
        const order = await getOrder(checkoutResult.orderId, true);
        vivrelleOrder = {
          ...order,
          shippingMethod: state.shippingMethodId || 'standard',
          isNYCPickup: state.shippingMethodCopy.includes('NYC Pickup')
            ? true
            : false,
          isHamptonsPickup: false,
          isSohoPickup: false,
          isNyfwPickup: false,
          isPickup: state.shippingMethodCopy.includes('NYC Pickup')
            ? true
            : false
        };
      }

      const fwrdLineItems = revolveCart.filter(
        (item) => item.provider?.toLowerCase().match(/fwrd/i)
      );
      const revolveLineItems = revolveCart.filter(
        (item) => item.provider?.toLowerCase().match(/revolve/i)
      );
      // Process Revolve items
      if (revolveCart.length > 0) {
        if (fwrdLineItems.length > 0) {
          fwrdInvoice = await createInvoice(
            user.paymentCustomerId,
            undefined,
            'fwrd'
          );
        }
        if (revolveLineItems.length > 0) {
          revolveInvoice = await createInvoice(
            user.paymentCustomerId,
            undefined,
            'revolve'
          );
        }
        const revolveOrderItems: RevolveOrderItem[] = revolveCart.map(
          (item) => ({
            code: item.sku || item.item_group_id,
            size: item.size || 'all', // size is required
            quantity: 1,
            store: item.provider?.toLowerCase().match(/revolve/i)
              ? 'revolve'
              : 'fwrd'
          })
        );

        const revolveOrderResponse = await submitRevolveOrder(
          revolveOrderItems
        );

        analytics.track('Ella AI Checkout Place Order - Clicked', {
          ...metadata,
          revolveOrderItems,
          vivrelleOrderItems: vivrelleOrder
        });

        if (
          revolveOrderResponse?.status === 'error' ||
          revolveOrderResponse?.status === 'partial'
        ) {
          await processRevolveOrderResponse(
            revolveOrderResponse,
            state.revolveCartId || '',
            revolveCart.map((item) => ({
              id: item.id || '',
              product: {
                sku: item.sku || item.item_group_id,
                size: item.size || 'all'
              }
            }))
          );
          errorSubmitOrder = Boolean(errorSubmitOrder)
            ? `${errorSubmitOrder}/Revolve/FWRD`
            : 'Revolve/FWRD';
        }

        // If revolver order is successful, pay the invoices
        if (
          revolveOrderResponse?.status === 'success' ||
          revolveOrderResponse?.status === 'partial'
        ) {
          const successfulItems = [
            ...(revolveOrderResponse?.results?.revolve?.data?.orders?.instock?.items?.map(
              (item) => ({
                invoice: item.id,
                store: 'revolve',
                sku: item.style
              })
            ) || []),
            ...(revolveOrderResponse?.results?.fwrd?.data?.orders?.instock?.items?.map(
              (item) => ({ invoice: item.id, store: 'fwrd', sku: item.style })
            ) || [])
          ];

          // Build per-store payments and await them together without throwing
          const fwrdItemsToPay = fwrdLineItems.filter((li) =>
            successfulItems
              .map((si) => si.sku)
              .includes(li?.sku || li?.item_group_id)
          );
          const revolveItemsToPay = revolveLineItems.filter((li) =>
            successfulItems
              .map((si) => si.sku)
              .includes(li?.sku || li?.item_group_id)
          );

          const payTasks: Promise<void>[] = [];

          if (fwrdInvoice?.invoice && fwrdItemsToPay.length > 0) {
            const fwrdTask = (async () => {
              try {
                fwrdInvoice.invoice = await payInvoice(
                  fwrdInvoice.invoice.id || '',
                  user.paymentCustomerId,
                  fwrdItemsToPay.map((item) => ({
                    description: `Item ${item.sku || item.item_group_id}`,
                    cartItemId: item.id || '',
                    quantity: 1
                  })),
                  newlyAttachedPaymentMethod
                    ? newlyAttachedPaymentMethod.id
                    : undefined,
                  state.revolveCartId || undefined
                );

                if (fwrdInvoice?.invoice.status !== 'paid') {
                  const fwrdProviderInvoice =
                    revolveOrderResponse?.results?.fwrd?.data?.orders?.instock
                      ?.invoice;
                  if (fwrdProviderInvoice) {
                    await cancelRevolveOrders([
                      {
                        invoice: fwrdProviderInvoice,
                        store: 'fwrd'
                      }
                    ]);
                  }
                  for (const item of fwrdItemsToPay) {
                    await removeItemFromCart(
                      state.revolveCartId || '',
                      item?.id || '',
                      'fwrd'
                    );
                  }
                  if (revolveOrderResponse.results?.fwrd) {
                    revolveOrderResponse.results.fwrd.message = 'cancelled';
                  }
                  errorSubmitOrder = Boolean(errorSubmitOrder)
                    ? `${errorSubmitOrder}/FWRD`
                    : 'FWRD';
                }
              } catch (_e) {
                // On error, attempt cancellation and cart cleanup; don't throw
                try {
                  const fwrdProviderInvoice =
                    revolveOrderResponse?.results?.fwrd?.data?.orders?.instock
                      ?.invoice;
                  if (fwrdProviderInvoice) {
                    await cancelRevolveOrders([
                      {
                        invoice: fwrdProviderInvoice,
                        store: 'fwrd'
                      }
                    ]);
                  }
                } catch {}
                for (const item of fwrdItemsToPay) {
                  try {
                    await removeItemFromCart(
                      state.revolveCartId || '',
                      item?.id || '',
                      'fwrd'
                    );
                  } catch {}
                }
                if (revolveOrderResponse.results?.fwrd) {
                  revolveOrderResponse.results.fwrd.message = 'cancelled';
                }
                errorSubmitOrder = Boolean(errorSubmitOrder)
                  ? `${errorSubmitOrder}/FWRD`
                  : 'FWRD';
              }
            })();
            payTasks.push(fwrdTask);
          }

          if (revolveInvoice?.invoice && revolveItemsToPay.length > 0) {
            const revolveTask = (async () => {
              try {
                revolveInvoice.invoice = await payInvoice(
                  revolveInvoice.invoice.id || '',
                  user.paymentCustomerId,
                  revolveItemsToPay.map((item) => ({
                    description: `Item ${item.sku || item.item_group_id}`,
                    cartItemId: item.id || '',
                    quantity: 1
                  })),
                  newlyAttachedPaymentMethod
                    ? newlyAttachedPaymentMethod.id
                    : undefined,
                  state.revolveCartId || undefined
                );

                if (
                  !revolveInvoice.invoice ||
                  revolveInvoice.invoice.status !== 'paid'
                ) {
                  const revolveProviderInvoice =
                    revolveOrderResponse?.results?.revolve?.data?.orders
                      ?.instock?.invoice;
                  if (revolveProviderInvoice) {
                    await cancelRevolveOrders([
                      {
                        invoice: revolveProviderInvoice,
                        store: 'revolve'
                      }
                    ]);
                  }
                  for (const item of revolveItemsToPay) {
                    await removeItemFromCart(
                      state.revolveCartId || '',
                      item?.id || '',
                      'revolve'
                    );
                  }
                  if (revolveOrderResponse.results?.revolve) {
                    revolveOrderResponse.results.revolve.message = 'cancelled';
                  }
                  errorSubmitOrder = Boolean(errorSubmitOrder)
                    ? `${errorSubmitOrder}/Revolve`
                    : 'Revolve';
                }
              } catch (_e) {
                // On error, attempt cancellation and cart cleanup; don't throw
                try {
                  const revolveProviderInvoice =
                    revolveOrderResponse?.results?.revolve?.data?.orders
                      ?.instock?.invoice;
                  if (revolveProviderInvoice) {
                    await cancelRevolveOrders([
                      {
                        invoice: revolveProviderInvoice,
                        store: 'revolve'
                      }
                    ]);
                  }
                } catch {}
                for (const item of revolveItemsToPay) {
                  try {
                    await removeItemFromCart(
                      state.revolveCartId || '',
                      item?.id || '',
                      'revolve'
                    );
                  } catch {}
                }
                if (revolveOrderResponse.results?.revolve) {
                  revolveOrderResponse.results.revolve.message = 'cancelled';
                }
                errorSubmitOrder = Boolean(errorSubmitOrder)
                  ? `${errorSubmitOrder}/Revolve`
                  : 'Revolve';
              }
            })();
            payTasks.push(revolveTask);
          }

          // Await all payments (and related cancellations) without throwing
          await Promise.allSettled(payTasks);
        }

        try {
          const res = await fetch('/api/cart/generic/checkout', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              cartId: state.revolveCartId || '',
              shippingAddress:
                addresses.find((addr) => !addr.isBilling && addr.isDefault) ??
                addresses.find((addr) => addr.isBilling === false),
              billingAddress:
                addresses.find((addr) => addr.isBilling && addr.isDefault) ??
                addresses.find((addr) => addr.isBilling === true),
              // Build external_ref safely for EP checkout: filter out falsy charges and
              // join the remaining values. If none exist, send `undefined` so the
              // server omits the field instead of sending "null, undefined".
              charge:
                [fwrdInvoice?.invoice?.charge, revolveInvoice?.invoice?.charge]
                  .filter((c): c is string => Boolean(c))
                  .join(', ') || undefined,
              revolveOrder: JSON.stringify(revolveOrderResponse)
            })
          });
          revolveOrder = await res.json();
        } catch (e) {
          console.error('Generic checkout request failed:', e);
          // Intentionally continue so we still reach order dispatch
        }

        if (fwrdInvoice && fwrdInvoice.invoice) {
          await restoreBalanceAndDiscounts(
            user.paymentCustomerId || '',
            fwrdInvoice?.removedDiscounts,
            fwrdInvoice?.removedCredit,
            fwrdInvoice?.customerCurrentBalance
          );
        } else {
          if (revolveInvoice && revolveInvoice.invoice) {
            await restoreBalanceAndDiscounts(
              user.paymentCustomerId || '',
              revolveInvoice?.removedDiscounts,
              revolveInvoice?.removedCredit,
              revolveInvoice?.customerCurrentBalance
            );
          }
        }
      }

      setIsPlacingOrder(false);

      if (!revolveOrder?.id && !vivrelleOrder) {
        dispatch({
          type: 'SET_BILL',
          payload: {
            bill: {
              ...state.bill,
              subtotal: 0,
              taxes: 0
            }
          }
        });
      }

      dispatch({
        type: 'SET_ORDER',
        payload: {
          showCartConfirmation: true,
          revolveOrder: revolveOrder,
          vivrelleOrder,
          errorSubmitOrder
        }
      });
    } catch (error) {
      console.error('Error during checkout process:', error);
      analytics.track('Ella AI Order Confirmation - Error Message', {
        ...metadata,
        errorMessage: error
      });
      setErrorMessageTemporarily(
        'An unexpected error occurred. Please try again.'
      );
      setIsPlacingOrder(false);
    }
  };

  if (isLoading && !removingItemId) {
    return <EllaCartSkeleton />;
  }

  return (
    <div className={cn('EllaCart', className)}>
      {vivrelleCart.length > 0 && (
        <>
          <div className='EllaCart-title'>
            <Text
              variant='title-xxs'
              uppercase
            >
              From Vivrelle
            </Text>

            <Select
              className='EllaCart-shipping-select'
              onChange={handleShippingSelect}
              value={state.shippingMethodId}
            >
              {shippingMethods.map((shippingMethod, index) => {
                return (
                  <option
                    data-shipping-copy={shippingMethod.label}
                    value={shippingMethod.id}
                    key={index}
                  >
                    {shippingMethod.label}
                  </option>
                );
              })}
            </Select>
          </div>

          {additionalCheckoutFieldLabel && (
            <div className='InputField EllaCart-additional-checkout-field'>
              <label
                className='InputField-label'
                htmlFor='EllaCart-additional-checkout-field'
              >
                {additionalCheckoutFieldLabel}
              </label>
              <input
                id='EllaCart-additional-checkout-field'
                className='InputField-input'
                type='text'
                required
                maxLength={MAX_ADDITIONAL_CHECKOUT_FIELD_LENGTH}
                value={additionalCheckoutFieldValue}
                onChange={(event) => {
                  setAdditionalCheckoutFieldValue(event.currentTarget.value);
                }}
              />
              {additionalCheckoutFieldHelperText && (
                <Text
                  className='EllaCart-additional-checkout-field-helper'
                  variant='text-s'
                >
                  {additionalCheckoutFieldHelperText}
                </Text>
              )}
            </div>
          )}

          <Text
            className='EllaCart-subheader'
            variant='text-s'
          >
            You&apos;ll receive a confirmation email from Vivrelle with the
            details for your item(s). Please note, if you&apos;re an existing
            member, your new order will be prepared once your pre-paid return
            label for the previous order is scanned and in transit.
          </Text>

          <div className='EllaCart-products'>
            {vivrelleCart.map((product) => {
              return (
                <EllaCartProduct
                  canRemoveItem={true}
                  handleRemoveItem={() => handleRemoveItem(product)}
                  key={product.id}
                  product={product}
                  isRemoving={removingItemId === product.id}
                  isPlacingOrder={isPlacingOrder}
                  onUpdateItem={handleUpdateItemSize}
                />
              );
            })}
          </div>
        </>
      )}

      {/* REVOLVE/FWRD ITEMS */}
      {revolveCart.length > 0 && (
        <>
          <div className='EllaCart-title'>
            <Text
              variant='title-xxs'
              uppercase
            >
              From REVOLVE & FWRD
            </Text>
            <Text variant='title-xxs-500-upper'>2-day shipping</Text>
          </div>

          <Text
            className='EllaCart-subheader'
            variant='text-s'
          >
            REVOLVE and/or FWRD will send you a separate email confirming the
            details of your order.
            <br />
            If you need to make changes, please contact the REVOLVE support team
            at{' '}
            <a
              href='https://www.revolve.com/contactus?d=Womens'
              className='Link link-s compact underline'
              rel='noopener noreferrer'
              target='_blank'
            >
              REVOLVE.com
            </a>{' '}
            or call <span style={{ textWrap: 'nowrap' }}>1-888-442-5830</span>{' '}
            for assistance. You may reach FWRD support at{' '}
            <a
              href='https://www.fwrd.com/fw/content/customercare/customer-care/'
              className='Link link-s compact underline'
              rel='noopener noreferrer'
              target='_blank'
            >
              FWRD.com
            </a>{' '}
            or <span style={{ textWrap: 'nowrap' }}>1-866-434-3169.</span>
          </Text>

          <div className='EllaCart-products'>
            {revolveCart.map((product) => {
              return (
                <EllaCartProduct
                  canRemoveItem={true}
                  handleRemoveItem={() => handleRemoveItem(product)}
                  key={product.id}
                  product={product}
                  isRemoving={removingItemId === product.id}
                  isPlacingOrder={isPlacingOrder}
                  onUpdateItem={handleUpdateItemSize}
                />
              );
            })}
          </div>
        </>
      )}

      <EllaCartSubtotal
        subtotal={state.bill.subtotal}
        shipping={state.bill.shipping}
        taxes={state.bill.taxes}
        discountAmountPercentage={state.bill.discountPercentage}
      />

      <EllaCartShippingBilling
        addresses={state.addresses}
        fixedShippingAddress={fixedShippingAddress}
        shippingMethod={state.shippingMethodCopy}
        canEdit={true}
        dispatch={dispatch}
        bill={state.bill}
        vivrelleCart={vivrelleCart}
        cartItems={cartItems}
        revolveCartId={state.revolveCartId ?? ''}
        setNewlyAttachedPaymentMethod={setNewlyAttachedPaymentMethod}
        cardDetails={
          paymentCard
            ? {
                brand: paymentCard?.brand,
                last4Digits: paymentCard?.last4
              }
            : null
        }
      />

      <div className='Ella-buttons-container'>
        <Button
          className='single'
          variant='pink-400'
          disabled={
            isPlacingOrder ||
            !!errorMessage ||
            !user ||
            !user.paymentCustomerId ||
            removingItemId !== null ||
            isAdditionalCheckoutFieldMissing
          }
          onClick={handleOrder}
        >
          Place order
          {isPlacingOrder && (
            <>
              {' '}
              <LoadingSpinner size={10} />
            </>
          )}
        </Button>
      </div>
      {errorMessage && (
        <Text
          className='error'
          variant='text-s'
        >
          {errorMessage}
        </Text>
      )}
    </div>
  );
};

export default EllaCart;
